import PropTypes from 'prop-types'
import React from 'react'
import classNames from 'classnames'
import { connect } from 'react-redux'

import union from 'lodash/union'

import {
    ButtonSmall,
    ButtonToggleSmall,
    Popover,
} from 'react-wot-bootstrap'

import DefaultTooltip from 'components/DefaultTooltip/DefaultTooltip'
import Nation from 'components/Nation/Nation'
import VehicleFilterSelect from 'components/VehicleFilterSelect/VehicleFilterSelect'
import VehiclesTable from 'components/VehiclesTable/VehiclesTable'
import VehicleType from 'components/VehicleType/VehicleType'
import { CANCEL } from 'helpers/i18n'
import { filterVehicles, sortVehicles } from 'helpers/vehicles'
import { vehiclesFilterClose, vehiclesFilterOpen } from 'state/actions/players_matching'
import { KEY_CODES, VEHICLE_TYPES, NATIONS_BY_REALM } from 'constants'

import {
    SELECT_VEHICLES,
    CAPTION,
    NATIONS_CAPTIONS,
    NATIONS_ALL_CAPTION,
    VEHICLE_FILTER_TOOLTIP,
    VEHICLE_TYPES_TOOLTIPS,
    VEHICLE_TYPES_TOOLTIP_BODY,
    FAVORITE_VEHICLE_TOOLTIP,
    FAVORITE_VEHICLE_TOOLTIP_BODY,
    NATIONS_FILTER_TOOOLTIP,
} from './constants'


import styles from './VehicleFilter.scss'

export class VehicleFilter extends React.PureComponent {
    static propTypes = {
        filter: PropTypes.shape({
            nation: PropTypes.string.isRequired,
            vehicleTypes: PropTypes.arrayOf(PropTypes.oneOf(VEHICLE_TYPES)),
            onlyFavorite: PropTypes.bool.isRequired,
            sortColumn: PropTypes.string.isRequired,
            sortOrder: PropTypes.oneOf(['asc', 'desc']),
        }).isRequired,
        isHighlighted: PropTypes.bool.isRequired,
        onSelect: PropTypes.func.isRequired,
        realm: PropTypes.string.isRequired,
        selectedVehicleCds: PropTypes.arrayOf(PropTypes.number).isRequired,
        vehicles: PropTypes.array.isRequired,

        vehiclesFilterClose: PropTypes.func.isRequired,
        vehiclesFilterOpen: PropTypes.func.isRequired,
    }

    constructor(props) {
        super(props)
        this.state = {
            opened: false,
            filter: { ...props.filter },
            selectedVehicleCds: props.selectedVehicleCds,
        }

        this.handleToggle = this.toggle.bind(this)
        this.apply = this.apply.bind(this)
        this.handleCancelClick = this.hide.bind(this)
        this.handleHeaderClick = this.handleHeaderClick.bind(this)
        this.handleNationChange = this.changeNation.bind(this)
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown)
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.onKeyDown)
    }

    componentDidUpdate(prevProps) {
        const { selectedVehicleCds, filter } = this.props
        if (selectedVehicleCds !== prevProps.selectedVehicleCds || filter !== prevProps.filter) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ selectedVehicleCds, filter })
        }
    }

    onKeyDown = event => {  // todo: remove it after WCC-202
        if (event.keyCode === KEY_CODES.ESCAPE && this.state.opened) {
            this.hide()
        }
    }

    beforeHide = () => this.reset()

    reset() {
        if (!this.state.opened) return

        this.setState({
            filter: { ...this.props.filter },
            opened: false,
            selectedVehicleCds: this.props.selectedVehicleCds,
        })
        this.props.vehiclesFilterClose()
    }

    toggle(e) {
        this._popover.toggle(e.currentTarget)
        if (this.state.opened) {
            this.reset()
        } else {
            this.setState({ opened: true })
            this.props.vehiclesFilterOpen()
        }
    }

    hide() {
        this._popover.hide()
        this.reset()
    }

    apply() {
        this.hide()
        this.props.onSelect(this.state.selectedVehicleCds, this.state.filter)
    }

    changeNation = value => {
        const filter = { ...this.state.filter, nation: value }
        this.setState(() => ({
            filter,
        }))
    }

    toggleVehicleType(value) {
        let vehicleTypes = [...this.state.filter.vehicleTypes]
        if (vehicleTypes.includes(value)) {
            vehicleTypes = vehicleTypes.filter(x => x !== value)
        } else {
            vehicleTypes.push(value)
        }
        const filter = { ...this.state.filter, vehicleTypes }
        this.setState({
            filter,
        })
    }

    toggleFavorite = () => {
        const filter = { ...this.state.filter, onlyFavorite: !this.state.filter.onlyFavorite }
        this.setState({
            filter,
        })
    }

    handleHeaderClick(column) {
        const sortOrder = this.state.filter.sortColumn === column ?
            this.state.filter.sortOrder === 'desc' ? 'asc' : 'desc' :
            'desc'

        this.setState({
            filter: {
                ...this.state.filter,
                sortColumn: column,
                sortOrder,
            },
        })
    }

    updateSelectedVehiclesCds = selectedVehicleCds => this.setState({ selectedVehicleCds })

    getSortedVehicles = () => {
        const { filter, selectedVehicleCds } = this.state
        const filteredVehicles = filterVehicles(this.props.vehicles, filter)

        return sortVehicles(
            filteredVehicles, filter.sortColumn, this.props.realm, filter.sortOrder === 'desc', selectedVehicleCds
        )
    }

    getNationSelectOptions() {
        const vehiclesNations = this.props.vehicles.map(vehicle => vehicle.nation)
        const availableNations = [...new Set(vehiclesNations)]
        const realmNations = NATIONS_BY_REALM[this.props.realm]

        const otherNations = realmNations.filter(n => !availableNations.includes(n))

        const nations = union(realmNations, otherNations)
        const nationSelectOptions = nations.map(item => {
            const value = item
            const caption = NATIONS_CAPTIONS[item]
            const icon = <Nation nation={item} realm={this.props.realm}/>

            return {
                value,
                caption,
                icon,
            }
        })

        nationSelectOptions.unshift({
            value: 'all_nations',
            caption: NATIONS_ALL_CAPTION,
            icon: <Nation nation={'all_nations'} />,
        })

        return nationSelectOptions
    }

    getVehicleTypesSelector() {
        return VEHICLE_TYPES.map(type => {
            const icon = (
                <span className={styles.vehicleTypeFilterIcon}>
                    <VehicleType type={type} mini />
                </span>
            )
            const isChecked = this.state.filter.vehicleTypes.includes(type)
            return (
                <span className={styles.vehicleType} key={type}>
                    <ButtonToggleSmall
                        caption={icon}
                        isChecked={isChecked}
                        onClick={() => this.toggleVehicleType(type)}
                        tooltipId={VEHICLE_TYPES_TOOLTIPS[type].id}
                    />
                    <DefaultTooltip
                        {...VEHICLE_TYPES_TOOLTIPS[type]}
                        message={isChecked ? VEHICLE_TYPES_TOOLTIP_BODY.OFF : VEHICLE_TYPES_TOOLTIP_BODY.ON}
                    />
                </span>
            )
        })
    }

    render() {
        const nationSelectOptions = this.getNationSelectOptions()
        const vehicleTypesSelector = this.getVehicleTypesSelector()

        const classNameBase = classNames(styles.base, {
            [styles.base__active]: this.props.isHighlighted,
        })
        return (
            <div
                className={classNameBase}
                onClick={handleInsideClick}
            >
                <ButtonSmall
                    type='miniSecondary'
                    tooltipId={VEHICLE_FILTER_TOOLTIP.id}
                    onClick={this.handleToggle}
                >
                    <div className={styles.filterButton} />
                </ButtonSmall>
                <Popover
                    ref={c => this._popover = c}
                    position={'right'}
                    alignment={'middle'}
                    width={390}
                    beforeHide={this.beforeHide}
                >
                    <h3 className={styles.popoverTitle}>{CAPTION}</h3>

                    <div>
                        <div className={styles.select} data-tip data-for={NATIONS_FILTER_TOOOLTIP.id}>
                            <VehicleFilterSelect
                                items={nationSelectOptions}
                                selectedItem={this.state.filter.nation}
                                onItemChange={this.handleNationChange}
                                width='auto'
                            />
                            <DefaultTooltip {...NATIONS_FILTER_TOOOLTIP} />
                        </div>
                        <div className={classNames(styles.select, styles.select__vehicleTypes)}>
                            {vehicleTypesSelector}
                        </div>
                        <div className={classNames(styles.select, styles.select__favorite)}>
                            <ButtonToggleSmall
                                caption={<span className={styles.favoriteVehicles} />}
                                isChecked={this.state.filter.onlyFavorite}
                                onClick={this.toggleFavorite}
                                tooltipId={FAVORITE_VEHICLE_TOOLTIP.id}
                            />
                            <DefaultTooltip
                                {...FAVORITE_VEHICLE_TOOLTIP}
                                message={this.state.filter.onlyFavorite ?
                                    FAVORITE_VEHICLE_TOOLTIP_BODY.OFF :
                                    FAVORITE_VEHICLE_TOOLTIP_BODY.ON
                                }
                            />
                        </div>
                    </div>
                    <VehiclesTable
                        isOpen={this.state.opened}
                        onHeaderClick={this.handleHeaderClick}
                        onSelectedVehicleCdsUpdate={this.updateSelectedVehiclesCds}
                        realm={this.props.realm}
                        selectedVehicleCds={this.state.selectedVehicleCds}
                        sortColumn={this.state.filter.sortColumn}
                        sortOrder={this.state.filter.sortOrder}
                        vehicles={this.getSortedVehicles()}
                    />

                    <div className={styles.popoverFooter}>
                        <div className={styles.button}>
                            <ButtonSmall
                                isJustified
                                isDisabled={!this.state.selectedVehicleCds.length}
                                onClick={this.apply}
                            >
                                {SELECT_VEHICLES(this.state.selectedVehicleCds.length)}
                            </ButtonSmall>
                        </div>
                        <div className={styles.button}>
                            <ButtonSmall type='secondary' onClick={this.handleCancelClick}>
                                {CANCEL}
                            </ButtonSmall>
                        </div>
                    </div>
                </Popover>
                <DefaultTooltip {...VEHICLE_FILTER_TOOLTIP} />
            </div>
        )
    }
}

export const mapStateToProps = ({ settings, players_matching }) => {
    const { vehicles, selected_vehicle_cds, vehicle_level, vehiclesFilter } = players_matching
    const { realm } = settings

    const vehiclesFilteredByLevel = vehicles.filter(
        vehicle => vehicle.level === vehicle_level
    )

    return {
        filter: vehiclesFilter,
        isHighlighted: vehiclesFilteredByLevel.length !== selected_vehicle_cds.length,
        vehicles: vehiclesFilteredByLevel,
        selectedVehicleCds: selected_vehicle_cds,
        realm,
    }
}

export const mapDispatchToProps = {
    vehiclesFilterOpen,
    vehiclesFilterClose,
}

export default connect(mapStateToProps, mapDispatchToProps)(VehicleFilter)

function handleInsideClick(e) {
    e.stopPropagation()
}



// WEBPACK FOOTER //
// ./components/VehicleFilter/VehicleFilter.jsx